import { RenderContext } from '@eventbrite/checkout-fairy-api';
import { updateHistory } from '@eventbrite/react-router-history';
import { configureStore } from '@eventbrite/redux-configure-store';
import { middleware as analyticsMiddleware } from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import { History } from 'history';
import get from 'lodash/get';
import { createMemoryHistory } from 'react-router';
import {
    push,
    routerMiddleware,
    syncHistoryWithStore,
} from 'react-router-redux';
import { Store } from 'redux';
import thunk from 'redux-thunk';
import { initializeEvent as initializeEventAction } from '../actions/event';
import {
    inflateAndResetApp as inflateAndResetAppAction,
    initializeApp as initializeAppAction,
} from '../actions/initialize';
import { RefundPolicyCode } from '../api/models/refundPolicy';
import { ONE_CHECKOUT, ORGANIZATION_VERTICALS } from '../constants';
import { dataLayerMiddleware } from '../middleware/dataLayerMiddleware';
import { formAnalytics } from '../middleware/formAnalytics';
import reducer from '../reducers';
import { CheckoutState } from '../state';
import { getValidThemeSettings } from '../utils/validation/complex/themeValidator';
import { initializePageAnalytics } from './analytics';
import { getInitialPath } from './routes';

interface createCheckoutStoreResponse {
    store: Store<CheckoutState>;
    initialPath: string;
    history: History;
}

interface CoreCheckoutContextProps {
    affiliateCode: $FixMe;
    app_name: string;
    app_version: string;
    campaignToken: $FixMe;
    childEvent?: RenderContext['childEvent'];
    current_timezone: string;
    captcha: $FixMe;
    countryWithPostalCode: $FixMe;
    defaultUserCountry: $FixMe;
    deferredPaymentDueDays: $FixMe;
    env: {
        currencyFormat: string;
        ebDomain: string;
        isEbuiTemplateRequest: boolean;
        isEmbeddedView: boolean;
        isMobile: boolean;
        locales: $FixMe;
        localeInfo: {
            locale: $FixMe;
            twitter_handle: $FixMe;
            facebook_locale: $FixMe;
            facebook_page: $FixMe;
            country_code: $FixMe;
        };
        serverUrl: string;
        signupNoReferrerUrl: string;
    };
    errors: $FixMe;
    event: $FixMe;
    eventDates?: RenderContext['eventDates'];
    existingOrderId: $FixMe;
    facebookOptions: $FixMe;
    featureFlags: $FixMe;
    gaSettings: $FixMe;
    guestId: $FixMe;
    grylls: { options: $FixMe; trackingData: $FixMe };
    hasDigitalContent: boolean;
    initialThemeSettings: $FixMe;
    inviteToken: $FixMe;
    inviteEmail: $FixMe;
    isEventbriteTLD: boolean;
    isGDPRCountry: boolean;
    isModal: boolean;
    isMyOrderFlow: boolean;
    isPayinsFlow: boolean;
    isProd: boolean;
    isRegistrationEvent: $FixMe;
    maxAllowedPayin: $FixMe;
    refundableBalance: $FixMe;
    refundPolicyCode: RefundPolicyCode;
    refundPolicyValidityDays?: number;
    request: $FixMe;
    requestPromoCode: $FixMe;
    showOrgMarketingOptIn: $FixMe;
    socialSSOSettings: $FixMe;
    squareApiHost: $FixMe;
    squareMarketAccount: $FixMe;
    splitIOProdKey: $FixMe;
    survey_info: $FixMe;
    teamSettings: $FixMe;
    teamToken: $FixMe;
    ticketAvailabilityInfo: $FixMe;
    ticketCallbackUrl: $FixMe;
    tickets: $FixMe;
    useAndroidProfile: boolean;
    useIosProfile: boolean;
    user: $FixMe;
    waitingRoom: $FixMe;
    waitlistCode: $FixMe;
    widgetParentUrl: $FixMe;
    urgencySignals: $FixMe;
}

export interface CheckoutStoreProps extends CoreCheckoutContextProps {
    parentLocale?: string;
}

export const createCheckoutStore = (
    props: CheckoutStoreProps,
    initialState?: CheckoutState,
): createCheckoutStoreResponse => {
    const historyManagement: History = createMemoryHistory();

    const middleware = [
        thunk,
        routerMiddleware(historyManagement),
        analyticsMiddleware(props, () => ({
            category: ONE_CHECKOUT,
            dimensions: {
                eventId: props.event.id,
                organizationVertical:
                    ORGANIZATION_VERTICALS[
                        get(
                            props,
                            'event.organization.vertical',
                        ) as keyof typeof ORGANIZATION_VERTICALS
                    ],
            },
        })),
        formAnalytics,
        dataLayerMiddleware,
    ];

    const store: Store<CheckoutState> = configureStore({
        reducer,
        initialState,
        middleware,
    });

    const initialData = getInitialData(props, store);
    store.dispatch(initializeAppAction(initialData));

    // If there is a "cached" session, and the user performs an action
    // such as changing the widget language, we only need to
    // get updated event data, but we should not reinitialize
    // all of the form data such as promo codes and ticket selection
    if (initialState) {
        store.dispatch(initializeEventAction(initialData));
    } else {
        store.dispatch(inflateAndResetAppAction(initialData));
    }
    // This is due to https://github.com/reactjs/react-router-redux/issues/534
    // syncHistoryWithStore fails to extract the correct location from the store
    // We need here to manually push it
    let initialPath = getInitialPath(store.getState());
    if (initialState) {
        const locationFromPreviousSession = get(
            initialState,
            'routing.locationBeforeTransitions.pathname',
        );
        if (locationFromPreviousSession) {
            initialPath = locationFromPreviousSession;
        }
    }
    const history: History = syncHistoryWithStore(historyManagement, store);
    initializePageAnalytics({
        historyManagement,
        store,
        appProps: props,
    });

    updateHistory(history, props.request);
    store.dispatch(push(initialPath));

    return { store, initialPath, history };
};

/*
    Wraps the eventDates payload inside a Record<yyyy_mm, DateWithSession[]> so it is easier to manage the calendar month datasets
*/
export const getEventDateWithSessionKeyValuePayload = (
    eventDates: RenderContext['eventDates'],
) => {
    const timedEntryEventsByMonth: Record<string, RenderContext['eventDates']> =
        {};
    if (eventDates?.length && eventDates[0].sessions[0]?.startDatetime) {
        const date = new Date(eventDates[0].sessions[0].startDatetime);
        const year = date.getFullYear();
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const month_year_key = `${year}_${month}`;
        timedEntryEventsByMonth[month_year_key] = eventDates;
    }
    return timedEntryEventsByMonth;
};

const getInitialData = (
    props: CheckoutStoreProps,
    store: Store<CheckoutState>,
) => {
    const state = store.getState();

    const {
        affiliateCode,
        app_name,
        app_version,
        captcha,
        countryWithPostalCode,
        current_timezone,
        defaultUserCountry,
        deferredPaymentDueDays,
        env: {
            isMobile,
            currencyFormat,
            locales,
            localeInfo: {
                locale,
                // every locale can use a different twitter account and
                // facebook account, so we need to use them in order to build
                // share messages
                twitter_handle: twitterHandle,
                facebook_locale: facebookLocale,
                facebook_page: facebookPage,
                country_code: envCountryCode,
            },
            serverUrl,
            signupNoReferrerUrl,
            ebDomain,
        },
        errors,
        event,
        eventDates,
        childEvent,
        existingOrderId,
        featureFlags,
        gaSettings,
        guestId,
        grylls: { options: gryllsOptions, trackingData: gryllsTrackingData },
        inviteToken,
        inviteEmail,
        isGDPRCountry,
        isMyOrderFlow,
        isPayinsFlow,
        campaignToken,
        isEventbriteTLD,
        hasDigitalContent,
        isModal,
        isProd,
        isRegistrationEvent: isRegEvent,
        maxAllowedPayin,
        parentLocale,
        refundableBalance,
        refundPolicyCode,
        refundPolicyValidityDays,
        request,
        requestPromoCode,
        showOrgMarketingOptIn,
        socialSSOSettings,
        squareApiHost,
        squareMarketAccount,
        splitIOProdKey,
        survey_info,
        teamSettings,
        teamToken,
        ticketAvailabilityInfo,
        tickets,
        user,
        waitingRoom,
        waitlistCode,
        widgetParentUrl,
        urgencySignals,
    } = props;

    const ticketCallbackUrl = props.ticketCallbackUrl
        ? props.ticketCallbackUrl
        : get(request, 'params.ticket_callback_url', props.ticketCallbackUrl);

    const themeSettings = getValidThemeSettings(props.initialThemeSettings);
    const useIosProfile =
        get(state, 'app.useIosProfile') || props.useIosProfile;
    const useAndroidProfile =
        get(state, 'app.useAndroidProfile') || props.useAndroidProfile;

    const facebookOptions = {
        ...props.facebookOptions,
        locale: facebookLocale,
        page: facebookPage,
    };

    const payins = {
        isPayinsFlow,
        maxAllowedPayin,
        refundableBalance,
    };

    const captchaConfig = {
        ...captcha,
        disableRecaptcha3: featureFlags.disableRecaptcha3,
    };

    const initialData = {
        affiliateCode,
        appName: app_name,
        appVersion: app_version,
        campaignToken,
        captcha: captchaConfig,
        currentTimezone: current_timezone,
        currencyFormat,
        defaultUserCountry,
        deferredPaymentDueDays,
        ebDomain,
        envCountryCode,
        errors,
        event: {
            ...event,
            survey_info,
            eventDates: getEventDateWithSessionKeyValuePayload(
                eventDates || [],
            ),
            childEventId: request?.params?.ceid,
            childEvent,
        },
        existingOrderId,
        facebookOptions,
        featureFlags,
        gaSettings,
        gryllsOptions,
        gryllsTrackingData,
        hasDigitalContent,
        guestId,
        inviteToken,
        inviteEmail,
        isEventbriteTLD,
        isGDPRCountry,
        isMobile,
        isModal,
        isMyOrderFlow,
        isProd,
        isRegEvent,
        locale: parentLocale || locale,
        locales,
        countryWithPostalCode,
        payins,
        refundPolicyCode,
        refundPolicyValidityDays,
        request,
        requestPromoCode,
        showOrgMarketingOptIn,
        serverUrl,
        signupNoReferrerUrl,
        splitIOProdKey,
        squareApiHost,
        squareMarketAccount,
        ssoSettings: socialSSOSettings,
        teamSettings,
        teamToken,
        themeSettings,
        ticketAvailabilityInfo,
        ticketCallbackUrl,
        tickets,
        twitterHandle,
        useAndroidProfile,
        useIosProfile,
        user,
        waitingRoom,
        waitlistCode,
        widgetParentUrl,
        urgencySignals,
    };

    return initialData;
};
